import { useLanguageContext } from "@@/settings/language-context";
import type { LanguageCode, TimeRange, Timezone } from "@towni/common";
import { asDate, getLocale, tz } from "@towni/common";
import { format, isSameMonth, isSameYear } from "date-fns";
import { useCallback } from "react";

const _formatAccommodationTimeRange =
    (language: LanguageCode, timezone: Timezone) =>
    (timeRange: TimeRange | undefined): string => {
        if (!timeRange) return "";

        const locale = getLocale(language);
        const start = asDate(timeRange.start, timezone);
        const end = asDate(timeRange.end, timezone);

        if (!start || !end) return "";

        // 1. If same month and year, show day-day month
        if (isSameMonth(start, end) && isSameYear(start, end)) {
            return `${format(start, "d", {
                locale,
            })} - ${format(end, "d MMM", {
                locale,
            })}`;
        }

        // 2. If different month but same year, show day month-day month
        else if (!isSameMonth(start, end) && isSameYear(start, end)) {
            return `${format(start, "d MMM", {
                locale,
            })} - ${format(end, "d MMM", {
                locale,
            })}`;
        }

        // 3. If different year, show day month year-day month year
        else {
            return `${format(start, "d MMM yyyy", {
                locale,
            })} - ${format(end, "d MMM yyyy", {
                locale,
            })}`;
        }
    };

const useAccommodationTimeRangeFormatter = (timezone = tz.EuropeStockholm) => {
    const { language } = useLanguageContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback(_formatAccommodationTimeRange(language, timezone), [
        language,
        timezone,
    ]);
};

export { useAccommodationTimeRangeFormatter };
